import React, { RefObject, useEffect, useLayoutEffect, useState } from "react";
import { clsx } from "clsx";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Button } from "../../design-system";
import classes from "./Tour.module.less";

export interface TourStep {
    title: string;
    text: string;
    /** Highlights [data-tour-id="<anchorId>"] in the app DOM. */
    anchorId?: string;
    /** Highlights the first match of this selector inside `iframeRef` (same-origin only). */
    frameSelector?: string;
}

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

interface TourProps {
    steps: TourStep[];
    open: boolean;
    onClose: () => void;
    /** Content iframe that frameSelector steps are resolved inside; omit when every step uses anchorId. */
    iframeRef?: RefObject<HTMLIFrameElement>;
}

/**
 * The quiet "Take a tour" affordance that opens a Tour, for the host screen's
 * chrome. Ships light-theme colors; pass a className to recolor for dark
 * surfaces.
 */
export const TourButton: React.FC<{ onClick: () => void; className?: string }> = ({
    onClick,
    className,
}) => (
    <button type="button" className={clsx(classes.tourButton, className)} onClick={onClick}>
        <QuestionCircleOutlined />
        Take a tour
    </button>
);

const PADDING = 8;
const CARD_GAP = 16;
const CARD_WIDTH = 380;

function placeCard(rect: Rect): React.CSSProperties {
    // Prefer right; fall back to below; clamp to viewport.
    const w = window.innerWidth;
    const h = window.innerHeight;
    let top = rect.top;
    let left = rect.left + rect.width + CARD_GAP;

    if (left + CARD_WIDTH > w - 16) {
        left = rect.left;
        top = rect.top + rect.height + CARD_GAP;
    }
    // Anchors near the right edge still overflow: pull the card back in.
    if (left + CARD_WIDTH > w - 16) {
        left = w - CARD_WIDTH - 16;
    }
    if (top + 280 > h - 16) {
        top = Math.max(16, h - 280 - 16);
    }
    if (top < 16) top = 16;
    if (left < 16) left = 16;
    return { top, left };
}

function pad(rect: Rect): Rect {
    return {
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
    };
}

/** contentDocument throws (or is null) when the iframe is cross-origin. */
function getFrameDocument(iframe: HTMLIFrameElement | null): Document | null {
    try {
        return iframe?.contentDocument ?? null;
    } catch {
        return null;
    }
}

function measureStep(step: TourStep, iframe: HTMLIFrameElement | null): Rect | null {
    if (step.anchorId) {
        const el = document.querySelector<HTMLElement>(`[data-tour-id="${step.anchorId}"]`);
        return el ? el.getBoundingClientRect() : null;
    }
    if (step.frameSelector) {
        if (!iframe) return null;
        const frameRect = iframe.getBoundingClientRect();
        const el = getFrameDocument(iframe)?.querySelector<HTMLElement>(step.frameSelector);
        if (!el) {
            // Cross-origin or selector missing: spotlight the whole frame block.
            return frameRect;
        }
        const r = el.getBoundingClientRect();
        // Translate into app-viewport coordinates, clipped to the visible iframe box.
        const top = Math.max(frameRect.top + r.top, frameRect.top);
        const left = Math.max(frameRect.left + r.left, frameRect.left);
        const bottom = Math.min(frameRect.top + r.top + r.height, frameRect.bottom);
        const right = Math.min(frameRect.left + r.left + r.width, frameRect.right);
        if (bottom <= top || right <= left) return frameRect;
        return { top, left, width: right - left, height: bottom - top };
    }
    return null;
}

export const Tour: React.FC<TourProps> = ({ steps, open, onClose, iframeRef }) => {
    const [step, setStep] = useState(0);
    // The card's text lags one measure behind `step`: it swaps only once the card
    // is heading to (or at) its new position, so the content never jumps ahead of the move.
    const [displayedStep, setDisplayedStep] = useState(0);
    const [rect, setRect] = useState<Rect | null>(null);

    const currentStep = steps[Math.min(step, steps.length - 1)];
    const displayedContent = steps[Math.min(displayedStep, steps.length - 1)];
    const hasTarget = Boolean(currentStep.anchorId || currentStep.frameSelector);

    useEffect(() => {
        if (open) {
            setStep(0);
            setDisplayedStep(0);
        }
    }, [open]);

    useLayoutEffect(() => {
        if (!open) {
            setRect(null);
            return;
        }
        if (!hasTarget) {
            setRect(null);
            setDisplayedStep(step);
            return;
        }
        // Keep the previous rect while the next target is measured so the card and
        // spotlight glide straight to their new position instead of snapping to center.

        // Bring the target into view: frame targets are all visible when the frame
        // is scrolled to the top (its header and sidenav are sticky).
        if (currentStep.frameSelector) {
            try {
                iframeRef?.current?.contentWindow?.scrollTo({ top: 0, behavior: "smooth" });
            } catch {
                // cross-origin; the fallback spotlights the whole iframe anyway
            }
        } else if (currentStep.anchorId) {
            document
                .querySelector(`[data-tour-id="${currentStep.anchorId}"]`)
                ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }

        let cancelled = false;
        let timer = 0;
        const tryMeasure = (attempt = 0) => {
            if (cancelled) return;
            const r = measureStep(currentStep, iframeRef?.current ?? null);
            if (!r) {
                // The iframe may still be loading; keep retrying briefly.
                if (attempt < 30) {
                    timer = window.setTimeout(() => tryMeasure(attempt + 1), 60);
                } else {
                    setDisplayedStep(step);
                }
                return;
            }
            // Measure once more after the smooth-scroll settles.
            timer = window.setTimeout(() => {
                if (cancelled) return;
                const settled = measureStep(currentStep, iframeRef?.current ?? null);
                if (settled) setRect(pad(settled));
                setDisplayedStep(step);
            }, 350);
        };
        tryMeasure();
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, step]);

    useEffect(() => {
        if (!open) return;
        const remeasure = () => {
            const r = measureStep(currentStep, iframeRef?.current ?? null);
            // Transient misses keep the last rect rather than flashing the card to center.
            if (r) setRect(pad(r));
        };
        const onKey = (e: KeyboardEvent) => {
            // Captured + stopped so Escape closes the tour without also reaching an
            // enclosing dialog's own Escape handler.
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose();
            }
            if (e.key === "ArrowRight") setStep((s) => Math.min(s + 1, steps.length - 1));
            if (e.key === "ArrowLeft") setStep((s) => Math.max(s - 1, 0));
        };
        window.addEventListener("resize", remeasure);
        window.addEventListener("scroll", remeasure, true);
        window.addEventListener("keydown", onKey, true);
        const frameWindow = (() => {
            try {
                return iframeRef?.current?.contentWindow ?? null;
            } catch {
                return null;
            }
        })();
        frameWindow?.addEventListener("scroll", remeasure, true);
        return () => {
            window.removeEventListener("resize", remeasure);
            window.removeEventListener("scroll", remeasure, true);
            window.removeEventListener("keydown", onKey, true);
            frameWindow?.removeEventListener("scroll", remeasure, true);
        };
    }, [open, currentStep, steps.length, onClose, iframeRef]);

    if (!open) return null;

    const isLast = step === steps.length - 1;

    return (
        <>
            <div
                className={`${classes.scrim} ${hasTarget && rect ? classes.behindSpotlight : ""}`}
                onClick={onClose}
            />
            {hasTarget && rect && (
                <div
                    className={classes.spotlight}
                    style={{
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                    }}
                />
            )}
            <div
                className={classes.card}
                style={
                    hasTarget && rect
                        ? placeCard(rect)
                        : { top: "40%", left: "50%", transform: "translate(-50%, -50%)" }
                }
            >
                <div key={displayedStep} className={classes.content}>
                    <div className={classes.step}>
                        Step {displayedStep + 1} of {steps.length}
                    </div>
                    <div className={classes.title}>{displayedContent.title}</div>
                    <div className={classes.text}>{displayedContent.text}</div>
                </div>
                <div className={classes.controls}>
                    <div className={classes.dots}>
                        {steps.map((_, i) => (
                            <span key={i} className={`${classes.dot} ${i === step ? classes.on : ""}`} />
                        ))}
                    </div>
                    <div className={classes.buttons}>
                        {step > 0 && (
                            <Button size="small" onClick={() => setStep(step - 1)}>
                                Back
                            </Button>
                        )}
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => (isLast ? onClose() : setStep(step + 1))}
                        >
                            {isLast ? "Done" : "Next"}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
